import React, { useMemo } from 'react';
import { marked } from 'marked';

import { escapeHtml } from '../utils/sanitize';

marked.setOptions({
    gfm: true,
    breaks: true,
});

type FormatProps = {
    children: string;
    className?: string;
    markdown?: boolean;
};

export default function Format(props: FormatProps) {
    const content = useMemo(() => {
        if (props.markdown) {
            return formatMarkdown(props.children);
        }

        return formatPlainText(props.children);
    }, [props.children, props.markdown]);

    return <>
        <div className={props.className} dangerouslySetInnerHTML={{ __html: content }} />
    </>;
}

export function formatPlainText(text: string): string {
    let result = escapeHtml(text);
    result = result.replace(/\n/g, '<br/>');

    return result;
}

export function formatMarkdown(text: string): string {
    let result = escapeHtml(text);

    result = marked.parse(result, { async: false }) as string;

    result = sanitizeHtml(result);

    return result;
}

function sanitizeHtml(html: string): string {
    const allowedTags = [
        'p', 'br', 'strong', 'em', 'code', 'pre', 'blockquote',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'hr',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'a', 'del', 'input',
    ];

    const allowedAttributes: Record<string, string[]> = {
        'a': ['href', 'target', 'rel'],
    };

    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
    const attrRegex = /([a-zA-Z][a-zA-Z0-9-]*)(?:="([^"]*)"|(?=\s|>|$))/g;

    return html.replace(tagRegex, (match, tagName) => {
        const tag = tagName.toLowerCase();

        if (!allowedTags.includes(tag)) {
            return escapeHtml(match);
        }

        if (match.startsWith('</')) {
            return `</${tag}>`;
        }

        if (tag === 'input') {
            const isCheckbox = match.includes('type="checkbox"');

            if (!isCheckbox) {
                return '';
            }

            const isChecked = match.includes('checked');

            return isChecked ? '<input type="checkbox" checked disabled/>' : '<input type="checkbox" disabled/>';
        }

        const allowed = allowedAttributes[tag] || [];
        let cleanTag = `<${tag}`;
        let attrMatch;

        while ((attrMatch = attrRegex.exec(match)) !== null) {
            const attrName = attrMatch[1].toLowerCase();
            const attrValue = attrMatch[2];

            if (!allowed.includes(attrName)) {
                continue;
            }

            if (attrName === 'href') {
                if (attrValue?.startsWith('http://') || attrValue?.startsWith('https://') || attrValue?.startsWith('mailto:')) {
                    cleanTag += ` href="${attrValue}" target="_blank" rel="noopener noreferrer"`;
                }

                continue;
            }

            if (attrValue !== undefined) {
                cleanTag += ` ${attrName}="${attrValue}"`;
            } else {
                cleanTag += ` ${attrName}`;
            }
        }

        attrRegex.lastIndex = 0;

        if (match.endsWith('/>')) {
            cleanTag += '/>';
        } else {
            cleanTag += '>';
        }

        return cleanTag;
    });
}
